import { createElement } from 'react';
import type { UseToastOptions } from '@chakra-ui/react';
import { gateForErrorCode } from '@bower/shared/billing/gates';
import {
  isTaskNotificationData,
  TASK_NOTIFICATION_TYPE,
} from '@bower/shared/constants/task-notification';
import type { TaskNotificationData } from '@bower/shared';

import type { invalidateEntityQueries } from '../../lib/queryClient';
import type { emitUpgradeGateBlocked } from '../../services/events/upgradeGateEvents';
import { requestNoteOcrFeedback } from '../../services/events/noteOcrFeedbackEvents';
import { ROUTES } from '../../constants/routes';
import { ProactiveToast } from '../../components/ui/ProactiveToast';
import { ocrWarningCopy } from '../../utils/ocrWarningCopy';
import {
  bulkFailureToast,
  bulkReadyToast,
  failureToast,
  isVideoInsightMode,
  readyToast,
  videoInsightFailureToast,
  videoInsightReadyToast,
  type BulkTaskKind,
  type CaptureTaskKind,
} from '../../utils/taskCopy';

/** The slice of a socket notification this handler reads. */
export interface TaskNotificationEvent {
  readonly type: string;
  readonly data?: unknown;
}

/** What the handler needs from `useNotificationSocket`, injected so it tests without a socket. */
export interface TaskNotificationDeps {
  readonly toast: (options: UseToastOptions) => void;
  readonly pathname: string;
  readonly isMobile: boolean;
  /** On the onboarding wizard, where a navigating toast would pull the user out of it. */
  readonly isOnboarding: boolean;
  readonly navigate: (path: string) => void;
  readonly getWorkspaceId: () => string | null;
  readonly invalidate: typeof invalidateEntityQueries;
  /** Refetch one video's `useVideoInsights` query (`videoInsightsKey`). */
  readonly invalidateVideoInsights: (videoArtifactId: string) => void;
  readonly emitUpgradeGate: typeof emitUpgradeGateBlocked;
}

const CAPTURE_KINDS: ReadonlySet<string> = new Set<CaptureTaskKind>([
  'note-transcription',
  'note-ocr',
]);

function isCaptureKind(kind: string): kind is CaptureTaskKind {
  return CAPTURE_KINDS.has(kind);
}

const BULK_KINDS: ReadonlySet<string> = new Set<BulkTaskKind>([
  'content-extraction',
  'note-from-file',
  'spreadsheet-import',
]);

function isBulkKind(kind: string): kind is BulkTaskKind {
  return BULK_KINDS.has(kind);
}

type Placement = Pick<UseToastOptions, 'position' | 'variant' | 'isClosable'>;

/**
 * The `task:complete` / `task:failed` pair (spec U-8) for the two note
 * captures, the three bulk kinds (spreadsheet import, file to note, file
 * reading) and video insights. Returns false for anything else, so the caller's bespoke branches
 * still see it. The event's title and message are placeholders: the copy comes
 * from `taskCopy.ts`. Capture toasts show on the note's own page too (U-18);
 * the bulk toasts follow the bespoke toasts they replace (see `bulkToast`).
 */
export function handleTaskNotification(
  event: TaskNotificationEvent,
  deps: TaskNotificationDeps,
): boolean {
  const isComplete = event.type === TASK_NOTIFICATION_TYPE.COMPLETE;
  if (!isComplete && event.type !== TASK_NOTIFICATION_TYPE.FAILED) return false;
  const data = event.data;
  if (!isTaskNotificationData(data)) return false;
  const kind = data.kind;
  const isVideoInsight = kind === 'video-insight';
  if (!isCaptureKind(kind) && !isBulkKind(kind) && !isVideoInsight) return false;
  // A task from a workspace the user has left: nothing on screen is about it.
  if (data.workspaceId !== deps.getWorkspaceId()) return true;

  if (isVideoInsight) {
    videoInsightToast(isComplete, data, deps);
    return true;
  }

  // The task's writes broadcast nothing; this is the only refresh signal.
  const scope = { workspaceId: data.workspaceId };
  // Both entity types are artifacts: this refreshes `artifacts.detail(id)`,
  // the key the bespoke spreadsheet `job:*` branch refreshed, plus the lists.
  deps.invalidate(data.entityType, 'updated', data.entityId, scope);
  if (data.parentId) deps.invalidate('artifact', 'updated', data.parentId, scope);
  // The note a file became: new, so the lists showing it must refetch.
  if (isComplete && data.resultId) deps.invalidate('artifact', 'created', data.resultId, scope);

  const placement = {
    position: deps.isMobile ? 'bottom' : 'bottom-right',
    variant: 'left-accent',
    isClosable: true,
  } as const satisfies UseToastOptions;

  // Quota: the upgrade modal carries the message, no toast (every kind).
  const gateKey = !isComplete && data.errorCode ? gateForErrorCode(data.errorCode) : null;
  if (gateKey) {
    deps.emitUpgradeGate(gateKey, {});
    return true;
  }

  if (isBulkKind(kind)) {
    bulkToast(kind, isComplete, data, deps, placement);
    return true;
  }

  if (!isComplete) {
    deps.toast({
      ...failureToast(kind, data.errorCode),
      status: 'error',
      duration: 5000,
      ...placement,
    });
    return true;
  }

  deps.toast({ ...readyOptions(kind, data), ...placement });
  requestFeedbackIfOnNote(kind, data, deps.pathname);
  return true;
}

/**
 * `video-insight` (spec "3c, decided 2026-10-03"): the toast the default
 * `job:complete` / `job:error` branch of `useNotificationSocket` shows today,
 * with the same placement, statuses and durations. Ahead of the upgrade gate
 * on purpose: no legacy `job:*` video-insight toast ever opened the upgrade
 * modal (that branch gates only import and OCR failures), so neither does this
 * one, even if a code like `QUOTA_EXHAUSTED` is ever mapped to a gate.
 *
 * The state write broadcasts on `/sync`, which refreshes the open panel
 * (`useVideoInsights`); this refresh covers a missed or late sync event.
 */
function videoInsightToast(
  isComplete: boolean,
  data: TaskNotificationData,
  deps: TaskNotificationDeps,
): void {
  deps.invalidateVideoInsights(data.entityId);
  const qualifier = data.slotKey.slice(`${data.kind}:`.length);
  const mode = isVideoInsightMode(qualifier) ? qualifier : null;
  const copy = isComplete ? videoInsightReadyToast(mode) : videoInsightFailureToast(data.errorCode);
  deps.toast({
    ...copy,
    status: isComplete ? 'success' : 'error',
    position: deps.isMobile ? 'bottom' : 'bottom-right',
    variant: 'left-accent',
    isClosable: true,
    duration: isComplete ? 3000 : 5000,
  });
}

/** Same statuses and durations as the bespoke `note:*-ready` toasts. */
function readyOptions(kind: CaptureTaskKind, data: TaskNotificationData): UseToastOptions {
  const copy = readyToast(kind);
  const warningCopy = kind === 'note-ocr' ? ocrWarningCopy(data.warningCodes ?? []) : undefined;
  if (warningCopy) {
    return { title: copy.title, description: warningCopy, status: 'warning', duration: 5000 };
  }
  return { title: copy.title, description: copy.description, status: 'success', duration: 3000 };
}

/** The accuracy prompt, only on that note's page and only with a trace to rate. */
function requestFeedbackIfOnNote(
  kind: CaptureTaskKind,
  data: TaskNotificationData,
  pathname: string,
): void {
  const noteId = data.parentId;
  const traceId = data.traceId;
  if (!noteId || !traceId || pathname !== ROUTES.note(noteId)) return;
  requestNoteOcrFeedback({
    noteId,
    traceId,
    category: kind === 'note-ocr' ? 'ocr_accuracy' : 'stt_accuracy',
  });
}

/**
 * The bulk kinds' toasts, each matching the bespoke toast it replaces:
 * - `spreadsheet-import`: the `job:*` ProactiveToast (5s ready, 8s failed),
 *   not shown on that spreadsheet's own page.
 * - `note-from-file`: ready is the `import:note-created` "View note"
 *   ProactiveToast (mobile route on mobile), not shown on the new note's page,
 *   on the source file's page, or during onboarding; failed is a plain error
 *   toast, shown during onboarding too (no action to leave the wizard), as the
 *   bespoke `import:note-failed` toast is.
 * - `content-extraction`: success is silent; failed is a plain error toast.
 */
function bulkToast(
  kind: BulkTaskKind,
  isComplete: boolean,
  data: TaskNotificationData,
  deps: TaskNotificationDeps,
  placement: Placement,
): void {
  if (kind === 'spreadsheet-import') {
    const route = ROUTES.spreadsheet(data.entityId);
    if (deps.pathname === route) return;
    const copy = isComplete ? bulkReadyToast(kind) : bulkFailureToast(kind, data.errorCode);
    if (!copy) return;
    showProactiveToast(deps, copy, isComplete ? 5000 : 8000, () => deps.navigate(route));
    return;
  }
  if (!isComplete) {
    deps.toast({
      ...bulkFailureToast(kind, data.errorCode),
      status: 'error',
      duration: 5000,
      ...placement,
    });
    return;
  }
  const copy = bulkReadyToast(kind);
  // Onboarding: the action would pull an unfinished account out of the wizard.
  if (!copy || deps.isOnboarding) return;
  const noteId = data.resultId;
  if (deps.pathname === ROUTES.attachment(data.entityId)) return;
  if (noteId && deps.pathname === ROUTES.note(noteId)) return;
  const onAction = noteId
    ? () => deps.navigate(deps.isMobile ? ROUTES.mobileNoteLegacy(noteId) : ROUTES.note(noteId))
    : undefined;
  showProactiveToast(deps, copy, 5000, onAction);
}

/** Same options as the bespoke ProactiveToasts: no variant, the card is its own. */
function showProactiveToast(
  deps: TaskNotificationDeps,
  copy: Readonly<{ title: string; actionLabel?: string }>,
  duration: number,
  onAction: (() => void) | undefined,
): void {
  deps.toast({
    position: deps.isMobile ? 'bottom' : 'bottom-right',
    duration,
    isClosable: true,
    render: ({ onClose }) =>
      createElement(ProactiveToast, {
        message: copy.title,
        actionLabel: onAction ? copy.actionLabel : undefined,
        onAction,
        onClose,
      }),
  });
}
